import Class from "./Class";
import Events from "./EventSource";
import dom from "./ui/dom";
import setImmediate from "bommediate";

export const EventSourceStreamer = new Class({
    Extends: Events,

    eventstream: null,
    listeners: null,

    initialize: function(url) {
        if (window.EventSource === void 0) {
            setImmediate(function() {
                this.trigger("error", new Error("EventSource not supported by browser"));
            }.bind(this));
            return;
        }

        const stream = this.eventstream = new window.EventSource(url);

        this.listeners = dom.listen(stream, {
            "error": function(error) {
                this.cancel();
                this.trigger("error", new Error(error));
                return false;
            }.bind(this),

            "message": function(evt) {
                this.trigger("data", JSON.parse(evt.data));
            }.bind(this),

            "done": function() {
                this.cancel();
                this.trigger("end");
            }.bind(this)
        },
            false);
    },

    cancel: function() {
        if (this.listeners) {
            this.listeners.destroy();
            this.listeners = null;
        }

        if (this.eventstream) {
            this.eventstream.close();
            this.eventstream = null;
        }
    }
});

const BaseStreamer = new Class({
    Extends: Events,

    mime: null,
    parsers: null,
    parser: null,
    _handled: null,
    url: "",
    accepts: "",

    initialize: function(url) {
        this.url = url;
        this.parsers = {};
        this._handled = 0;
    },

    registerParser: function(mimetypes, fn) {
        const parsers = this.parsers;
        fn = fn.bind(this);

        mimetypes = Array.isArray(mimetypes) ? mimetypes : [mimetypes];

        let accepts = this.accepts;
        mimetypes.forEach(function(mimetype) {
            parsers[mimetype] = (parsers[mimetype] || []).concat(fn);
            if (accepts.length > 0) {
                accepts += ",";
            }
            accepts += mimetype;
        });

        this.accepts = accepts;
    },

    _getParser: function(mimetype, sampledata) {
        const allParsers = this.parsers;
        let parsers = allParsers[mimetype] || allParsers[mimetype.split(";")[0]], createParser;

        if (parsers === void 0) {
            parsers = [];
            Object.keys(allParsers).forEach(function(mime) {
                allParsers[mime].forEach(function(parser) {
                    parsers.push(parser);
                });
            });
        }

        for (let i = 0; i < parsers.length; i++) {
            try {
                createParser = parsers[i];
                if (sampledata) {
                    createParser().parse(sampledata);
                }

                return createParser();
            } catch (e) {
                // Ignore this parser
            }
        }
    },

    _handleItems: function(items) {
        items = Array.isArray(items) ? items : [items];
        items.forEach(function(item) {
            if (item) {
                this.trigger("data", item);
            }
        }.bind(this));
    },

    _parse: function(data) {
        if (!this._parser) {
            const parser = this._getParser(this.mime, data);

            if (!parser) {
                this.trigger("error", new Error("Could not parse data: " + data));
                return [];
            }

            this._parser = parser;
        }

        return this._parser.parse(data);
    },

    cancel: function() {
        if (this._parser && this._parser.onDone) {
            this._handleItems(this._parser.onDone());
        }

        this._parser = null;
        setImmediate(function() {
            this.trigger("end");
        }.bind(this));
    }
});

const STATE = {
    UNSENT: 0,
    OPENED: 1,
    HEADERS_RECEIVED: 2,
    LOADING: 3,
    DONE: 4
};

const XHRStreamer = new Class({
    Extends: BaseStreamer,

    _handleTimeout: null,
    xhr: null,
    listeners: null,

    initialize: function(url, options) {
        BaseStreamer.apply(this, arguments);

        this.xhr = new XMLHttpRequest();
        this.xhr.open("GET", url);

        this.listeners = dom.listen(this.xhr, "readystatechange", this._readyStateHandle.bind(this));

        setImmediate(function() {
            this.xhr.setRequestHeader("Accept", this.accepts);
            this.xhr.send();
        }.bind(this));
    },

    _readyStateHandle: function() {
        const state = this.xhr.readyState;

        if (state === STATE.HEADERS_RECEIVED) {
            this.mime = this.xhr.getResponseHeader("Content-Type");
        }

        if (state === STATE.LOADING && this._handleTimeout === null) {
            this._handleTimeout = setTimeout(this._handleResponse.bind(this), 10);
        }

        if (state === STATE.DONE) {
            if (this.xhr.status !== 200) {
                this.trigger("error", new Error(this.xhr.statusText || "Connection error"));
                this.cancel();
            } else {
                clearTimeout(this._handleTimeout);
                this._handleResponse();
                this.cancel();
            }
        }
    },

    _handleResponse: function() {
        const data = this.xhr.responseText.trim(), done = this.xhr.readyState === STATE.DONE;

        const rows = data
            .slice(this._handled)
            .split("\n")
            .map(function(row) {
                return row.trim();
            })
            .filter(function(row) {
                return row !== "";
            });

        this._handled = Math.max(0, data.lastIndexOf("\n"));

        if (!done) {
            // ignore the last line because it is likely incomplete
            rows.pop();
        }

        const self = this;
        rows.forEach(function(row, i) {
            self._handleItems(self._parse(row));
        });

        this._handleTimeout = null;
    },

    cancel: function() {
        BaseStreamer.prototype.cancel.call(this);

        if (this._handleTimeout) {
            clearTimeout(this._handleTimeout);
        }

        if (this.listeners) {
            this.listeners.destroy();
            this.listeners = null;
        }

        if (this.xhr) {
            this.xhr.abort();
            this.xhr = null;
        }
    }
});

export const JSONStreamer = (function() {
    const base = XHRStreamer;

    return new Class({
        Extends: base,
        initialize: function() {
            base.apply(this, arguments);

            this.registerParser([
                "application/json;boundary=NL",
                "application/json"
            ], function() {
                return {
                    parse: JSON.parse,
                    onDone: null // optional cleanup phase
                };
            });
        }
    });
})();

const BuiltinStreamers = {
    "event-stream": EventSourceStreamer,
    "xhr": JSONStreamer
};

export const MultiStreamer = new Class({
    Extends: Events,

    listeners: null,
    streamListeners: null,
    streamer: null,
    streamers: null,

    initialize: function(url, options) {
        options = options || {};
        this.url = url;

        this.streamers = (options.streamers || Object.keys(BuiltinStreamers).sort()).slice();
        const _listeners = {};

        ["onData", "onEnd", "onError"].forEach(function(event) {
            if (options[event]) {
                _listeners[event.slice(2).toLowerCase()] = options[event];
            }
        });

        this.listeners = this.listen(_listeners);

        this._next();
    },

    _next: function() {
        if (this.streamListeners) {
            this.streamListeners.destroy();
        }

        if (this.streamer) {
            this.streamer.cancel();
        }

        let Streamer = this.streamers.shift();
        if (typeof Streamer === "string") {
            Streamer = BuiltinStreamers[Streamer];
        }

        if (Streamer !== void 0) {
            this.streamer = new Streamer(this.url);
            this.streamListeners = this.streamer.listen({
                "end": function() {
                    this.cancel();
                }.bind(this),

                "data": function(data) {
                    this.trigger("data", data);
                }.bind(this),

                "error": function() {
                    this._next();
                }.bind(this)
            });
        } else {
            this.trigger("error", new Error("Failed to load data from " + this.url));
        }
    },

    cancel: function() {
        if (this.streamListeners) {
            this.streamListeners.destroy();
            this.streamListeners = null;
        }

        this.trigger("end");

        if (this.listeners) {
            this.listeners.destroy();
            this.listeners = null;
        }
    }
});

export default {
    MultiStreamer: MultiStreamer,
    EventSourceStreamer: EventSourceStreamer,
    JSONStreamer: JSONStreamer
};



// WEBPACK FOOTER //
// ./src/abusesa-js/DataStreamer.js